import { z } from 'zod'

export const reminderEventTypeSchema = z.enum([
  'daily_free_session',
  'donation_campaign',
  'membership_welcome',
  'membership_expiry_warning',
  'membership_upgrade_confirmation',
])
export type ReminderEventType = z.infer<typeof reminderEventTypeSchema>

export const reminderTriggerTypeSchema = z.enum([
  'one_hour_before',
  'session_start',
  'campaign_update',
  'renewal_alert',
  'instant_confirmation',
])
export type ReminderTriggerType = z.infer<typeof reminderTriggerTypeSchema>

export const reminderStatusSchema = z.enum([
  'pending',
  'sent',
  'failed',
  'skipped',
])
export type ReminderStatus = z.infer<typeof reminderStatusSchema>

export const reminderLanguageSchema = z.enum(['en', 'hi'])
export type ReminderLanguage = z.infer<typeof reminderLanguageSchema>

export const metaTemplateStatusSchema = z.enum([
  'draft',
  'pending_approval',
  'approved',
  'rejected',
])
export type MetaTemplateStatus = z.infer<typeof metaTemplateStatusSchema>

export const reminderTemplateVariableSchema = z.object({
  key: z.string().min(1),
  description: z.string().min(1),
})
export type ReminderTemplateVariable = z.infer<
  typeof reminderTemplateVariableSchema
>

export const adminReminderTemplateSchema = z.object({
  id: z.string(),
  event_type: reminderEventTypeSchema,
  trigger_type: reminderTriggerTypeSchema,
  language: reminderLanguageSchema,
  template_name: z.string(),
  body_text: z.string(),
  variables: z.array(reminderTemplateVariableSchema),
  is_active: z.boolean(),
  meta_status: metaTemplateStatusSchema,
  updated_at: z.string(),
})
export type AdminReminderTemplate = z.infer<typeof adminReminderTemplateSchema>

export const adminReminderConfigResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    rollout_pct: z.number().int().min(0).max(100),
    event_types: z.array(
      z.object({
        event_type: reminderEventTypeSchema,
        is_enabled: z.boolean(),
        updated_by: z.string().nullable().optional(),
        updated_at: z.string(),
      }),
    ),
    templates: z.array(adminReminderTemplateSchema),
    stats: z.object({
      sent_today: z.number().int().nonnegative(),
      failed_today: z.number().int().nonnegative(),
      pending: z.number().int().nonnegative(),
    }),
  }),
})
export type AdminReminderConfigResponse = z.infer<
  typeof adminReminderConfigResponseSchema
>

export const adminReminderTemplateUpdatePayloadSchema = z
  .object({
    event_type: reminderEventTypeSchema.optional(),
    trigger_type: reminderTriggerTypeSchema.optional(),
    body_text: z.string().min(1).optional(),
    variables: z.array(reminderTemplateVariableSchema).optional(),
    is_active: z.boolean().optional(),
    language: reminderLanguageSchema.optional(),
    meta_status: metaTemplateStatusSchema.optional(),
  })
  .strict()
export type AdminReminderTemplateUpdatePayload = z.infer<
  typeof adminReminderTemplateUpdatePayloadSchema
>

export const adminReminderLogsResponseSchema = z.object({
  success: z.literal(true),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  per_page: z.number().int().positive(),
  data: z.array(
    z.object({
      id: z.string(),
      idempotency_key: z.string(),
      user_id: z.string(),
      event_type: reminderEventTypeSchema,
      event_id: z.string(),
      trigger_type: reminderTriggerTypeSchema,
      status: reminderStatusSchema,
      phone_number: z.string(),
      language: reminderLanguageSchema,
      template_name: z.string(),
      template_vars: z.unknown(),
      retry_count: z.number().int().nonnegative(),
      error_message: z.string().nullable(),
      meta_message_id: z.string().nullable(),
      scheduled_at: z.string(),
      sent_at: z.string().nullable(),
      created_at: z.string(),
    }),
  ),
})
export type AdminReminderLogsResponse = z.infer<
  typeof adminReminderLogsResponseSchema
>
